/**
 * Persisted report -> the framework body that renders it.
 *
 * Each of the five bodies is strongly typed against a narrow mirror of the
 * server schema, but `strategic_analysis_runs.report` arrives as jsonb — that
 * is, `unknown`. Something has to decide whether a given blob is a report of the
 * type this tab is showing. This is that something, and it is deliberately the
 * only place a report is cast.
 *
 * WHY IT GUARDS RATHER THAN CASTS. A bare cast makes a pipeline bug — or an old
 * run written under a previous schema — into a crash inside a body component
 * reading `body.forces.map`, which the ErrorBoundary turns into a blank screen
 * for the whole route. Withholding just the body keeps the status card, the
 * limitations band and the methodology modal, all of which still describe the
 * run truthfully.
 *
 * The guards test the fields each body actually destructures, not every field of
 * the type. They are a shape check against the wrong framework's report or a
 * half-written one, not a schema validator — the server already validated this
 * before publishing, and duplicating that here would be a second definition of
 * the same contract, free to drift.
 */

import React from 'react';
import type { ReadyReport } from '@/lib/strategic-analysis/report-contract';
import type {
  ClaimView,
  ExitThesis,
  MarketAssumptions,
} from '@/lib/strategic-analysis/types';
import { CompetitionAnalysis } from './CompetitionAnalysis';
import { PorterFiveForces } from './PorterFiveForces';
import { PestelAnalysis } from './PestelAnalysis';
import { MarketValidation } from './MarketValidation';
import { ExitStrategyAnalysis } from './ExitStrategyAnalysis';

interface AnalysisBodyProps {
  /**
   * The parsed report, WITH its analysis type still attached.
   *
   * One value rather than two, and that is the whole correction. This component
   * used to take `analysisType` and a separate `unknown` body, test two
   * top-level containers with `Array.isArray`, and then `as unknown as` its way
   * to a typed body — five casts, each one a place where the pairing of "which
   * analysis" with "which shape" was asserted rather than known. A discriminated
   * union carries the pairing, so the switch below narrows both together and the
   * compiler refuses a body handed to the wrong component.
   */
  report: ReadyReport;
  claims: ClaimView[];
  /** The BOUND assumption version this run read, never today's latest. */
  assumptions?: MarketAssumptions | null;
  /** The EXACT thesis version the assessment read, never today's latest. */
  assessedThesis?: ExitThesis | null;
  onInspectClaim?: (claim: ClaimView) => void;
  /** Absent for anyone who may not edit. Presence is what shows the control. */
  onEditAssumptions?: () => void;
  onEditThesis?: () => void;
  /** Injected so PESTEL freshness is deterministic under test. */
  now?: number;
  /** The analysed company's name, for the positioning map's own marker. */
  startupName?: string;
}

/**
 * NO GUARDS AND NO CASTS.
 *
 * The parse happened at the boundary. Re-checking here would be a second,
 * weaker definition of the same contract — which is what the two `Array.isArray`
 * tests were, and they passed for every malformed body whose top-level
 * containers happened to be arrays.
 */
export function AnalysisBody({
  report,
  claims,
  assumptions = null,
  assessedThesis = null,
  onInspectClaim,
  onEditAssumptions,
  onEditThesis,
  now,
  startupName,
}: AnalysisBodyProps) {
  switch (report.analysisType) {
    case 'competition':
      return (
        <CompetitionAnalysis
          body={report.body}
          claims={claims}
          startupName={startupName}
          onInspectClaim={onInspectClaim}
        />
      );

    case 'porter':
      return <PorterFiveForces body={report.body} claims={claims} onInspectClaim={onInspectClaim} />;

    case 'pestel':
      return (
        <PestelAnalysis
          body={report.body}
          claims={claims}
          onInspectClaim={onInspectClaim}
          now={now}
        />
      );

    case 'market_validation':
      return (
        <MarketValidation
          body={report.body}
          assumptions={assumptions}
          claims={claims}
          onInspectClaim={onInspectClaim}
          onEditAssumptions={onEditAssumptions}
        />
      );

    case 'exit_strategy':
      return (
        <ExitStrategyAnalysis
          body={report.body}
          assessedThesis={assessedThesis}
          claims={claims}
          onEditThesis={onEditThesis}
          onInspectClaim={onInspectClaim}
        />
      );
  }
}
